import { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Field, Input, Label } from '@shared/ui/Input';
import { Button } from '@shared/ui/Button';
import {
  apiErrorText,
  useLazyLookupCounterpartyByInnQuery,
  useCreateCounterpartyMutation,
  useListCounterpartiesQuery,
} from '@shared/api';
import { counterpartyStatusLabel, counterpartyStatusTone } from '@shared/lib/statusLabels';
import { Badge } from '@shared/ui/Badge';
import { useDebouncedValue } from '@shared/lib/useDebouncedValue';
import { useAgentPermission } from '@entities/user';

const Hint = styled.div<{ $tone?: 'info' | 'warn' | 'good' | 'danger' }>`
  font-size: 12px;
  margin-top: 6px;
  padding: 8px 10px;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme, $tone }) =>
    $tone === 'danger'
      ? theme.colors.status.danger + '14'
      : $tone === 'warn'
        ? theme.colors.status.warning + '14'
        : $tone === 'good'
          ? theme.colors.status.success + '14'
          : theme.colors.surface};
  color: ${({ theme, $tone }) =>
    $tone === 'danger'
      ? theme.colors.status.danger
      : $tone === 'warn'
        ? theme.colors.status.warning
        : $tone === 'good'
          ? theme.colors.status.success
          : theme.colors.text.secondary};
  border-left: 3px solid
    ${({ theme, $tone }) =>
      $tone === 'danger'
        ? theme.colors.status.danger
        : $tone === 'warn'
          ? theme.colors.status.warning
          : $tone === 'good'
            ? theme.colors.status.success
            : theme.colors.border};
  line-height: 1.4;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const SearchWrap = styled.div`
  position: relative;
`;

const SearchResults = styled.ul`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  max-height: 280px;
  overflow-y: auto;
  margin: 0;
  padding: 4px;
  background: ${({ theme }) => theme.colors.backgroundElevated};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  box-shadow: ${({ theme }) => theme.shadow.md};
  list-style: none;
  z-index: 20;
`;

const SearchItem = styled.li`
  padding: 8px 10px;
  border-radius: ${({ theme }) => theme.radius.sm};
  cursor: pointer;
  font-size: 13px;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.text.primary};

  &:hover,
  &:focus {
    background: ${({ theme }) => theme.colors.primarySoft};
    outline: none;
  }
`;

const SearchSecondary = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-top: 2px;
`;

interface Props {
  /** ID привязанного контрагента (если уже выбран). */
  counterpartyId: string | null;
  /** Контролируемое имя контрагента (для отображения). */
  counterpartyName: string;
  onChange: (data: { id: string | null; name: string }) => void;
  /** Можно ли создавать новых контрагентов прямо отсюда. */
  allowCreate?: boolean;
}

/**
 * подбор контрагента по ИНН.
 * Поведение:
 * - Пользователь вводит ИНН (только цифры, 10/12);
 * - С debounce 400мс дёргает `/lookup-inn` — поиск в локальном реестре;
 * - Если найден локально → автоматически привязывается, показываем статус;
 * - Если не найден, но ЕГРЮЛ доступен → кнопка «Создать и подтянуть из ЕГРЮЛ»;
 * - Если ЕГРЮЛ не настроен → возможность создать руками (имя).
 *
 * Бэкенд кэширует, поэтому повторные lookup по одному ИНН — мгновенные.
 */
export function CounterpartyPicker({
  counterpartyId,
  counterpartyName,
  onChange,
  allowCreate = true,
}: Props) {
  const { can_write } = useAgentPermission('statut');
  const [inn, setInn] = useState('');
  const [debouncedInn, setDebouncedInn] = useState('');
  const [lookup, { data, isFetching }] = useLazyLookupCounterpartyByInnQuery();
  const [create, { isLoading: isCreating }] = useCreateCounterpartyMutation();
  const [createError, setCreateError] = useState<string | null>(null);
  const lastLookupRef = useRef<string>('');

  // Search-as-you-type по реестру контрагентов (ADR-0001: юрист часто помнит
  // название, а не ИНН подрядчика). Скрывается как только выбран
  // counterpartyId — список становится визуальным шумом.
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebouncedValue(searchQuery, 300);
  const [searchOpen, setSearchOpen] = useState(false);
  const searchEnabled =
    !counterpartyId && debouncedSearch.trim().length >= 2;
  const { data: searchData } = useListCounterpartiesQuery(
    searchEnabled
      ? { search: debouncedSearch.trim(), limit: 8 }
      : undefined,
    { skip: !searchEnabled },
  );
  const searchResults = searchEnabled ? (searchData?.items ?? []) : [];

  // Debounce 400ms — экономим запросы при наборе.
  useEffect(() => {
    const t = window.setTimeout(() => {
      setDebouncedInn(inn);
    }, 400);
    return () => window.clearTimeout(t);
  }, [inn]);

  // Lookup только когда ИНН в валидной форме (10 или 12 цифр) и не повторяет
  // последний запрос.
  useEffect(() => {
    if (
      (debouncedInn.length === 10 || debouncedInn.length === 12) &&
      debouncedInn !== lastLookupRef.current
    ) {
      lastLookupRef.current = debouncedInn;
      lookup(debouncedInn);
    }
  }, [debouncedInn, lookup]);

  // Если поиск нашёл локальную запись — автоматически выбираем её.
  useEffect(() => {
    if (data?.source === 'local' && data.counterparty) {
      onChange({
        id: data.counterparty.id,
        name: data.counterparty.short_name ?? data.counterparty.full_name,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const innValid = debouncedInn.length === 10 || debouncedInn.length === 12;
  const noLocalMatch = !!data && data.source !== 'local' && !data.counterparty;
  const egrulAvailable = data?.source === 'egrul_available';

  async function onCreate() {
    setCreateError(null);
    try {
      const c = await create({
        inn: debouncedInn,
        full_name: counterpartyName.trim() || undefined,
        auto_lookup: true,
      }).unwrap();
      onChange({
        id: c.id,
        name: c.short_name ?? c.full_name,
      });
    } catch (e) {
      setCreateError(apiErrorText(e, 'Не удалось создать контрагента'));
    }
  }

  const localFound = useMemo(() => {
    if (data?.source !== 'local' || !data.counterparty) return null;
    return data.counterparty;
  }, [data]);

  // Сразу подсчитаем сообщение об опасности.
  const dangerHint = (() => {
    if (!localFound) return null;
    switch (localFound.status) {
      case 'LIQUIDATED':
      case 'BANKRUPT':
        return {
          tone: 'danger' as const,
          text: `⚠️ Контрагент ${counterpartyStatusLabel(localFound.status).toLowerCase()}. Заключение договора крайне рискованно.`,
        };
      case 'LIQUIDATING':
      case 'REORGANIZING':
        return {
          tone: 'warn' as const,
          text: `⚠️ Контрагент ${counterpartyStatusLabel(localFound.status).toLowerCase()}. Проверьте статус процедуры и полномочия подписанта.`,
        };
      default:
        return null;
    }
  })();

  function pickFromSearch(item: {
    id: string;
    inn: string | null;
    short_name: string | null;
    full_name: string;
  }) {
    onChange({ id: item.id, name: item.short_name ?? item.full_name });
    setInn(item.inn ?? '');
    setDebouncedInn(item.inn ?? '');
    lastLookupRef.current = item.inn ?? '';
    setSearchQuery('');
    setSearchOpen(false);
  }

  return (
    <>
      {/* Typeahead-поиск по реестру: юрист часто помнит название, а не ИНН.
          После выбора подставляется и ИНН, и имя — оба поля ниже становятся
          read-only. */}
      {!counterpartyId && (
        <Field>
          <Label htmlFor="cp-search-picker">Найти в реестре</Label>
          <SearchWrap>
            <Input
              id="cp-search-picker"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setSearchOpen(true);
              }}
              onFocus={() => setSearchOpen(true)}
              onBlur={() => {
                // setTimeout — даём успеть mousedown по элементу списка
                // зарегистрироваться раньше, чем blur схлопнет dropdown.
                window.setTimeout(() => setSearchOpen(false), 150);
              }}
              placeholder="Часть названия или ИНН — выберу из контрагентов"
              autoComplete="off"
            />
            {searchOpen && searchResults.length > 0 && (
              <SearchResults role="listbox">
                {searchResults.map((c) => (
                  <SearchItem
                    key={c.id}
                    role="option"
                    aria-selected="false"
                    tabIndex={0}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      pickFromSearch({
                        id: c.id,
                        inn: c.inn,
                        short_name: c.short_name,
                        full_name: c.full_name,
                      });
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        pickFromSearch({
                          id: c.id,
                          inn: c.inn,
                          short_name: c.short_name,
                          full_name: c.full_name,
                        });
                      }
                    }}
                  >
                    <Row>
                      <strong>{c.short_name ?? c.full_name}</strong>
                      <Badge $tone={counterpartyStatusTone(c.status)}>
                        {counterpartyStatusLabel(c.status)}
                      </Badge>
                    </Row>
                    <SearchSecondary>
                      ИНН {c.inn ?? '—'}
                      {c.short_name && c.short_name !== c.full_name && (
                        <> · {c.full_name}</>
                      )}
                    </SearchSecondary>
                  </SearchItem>
                ))}
              </SearchResults>
            )}
            {searchEnabled && searchOpen && searchResults.length === 0 && (
              <Hint>
                Никого не нашли в реестре. Введите ИНН ниже — подтянем из ЕГРЮЛ.
              </Hint>
            )}
          </SearchWrap>
        </Field>
      )}

      <Field>
        <Label htmlFor="cp-inn-picker">ИНН контрагента *</Label>
        <Input
          id="cp-inn-picker"
          value={inn}
          onChange={(e) => {
            const v = e.target.value.replace(/\D/g, '').slice(0, 12);
            setInn(v);
            // Сброс привязки при изменении ИНН
            if (counterpartyId) onChange({ id: null, name: counterpartyName });
          }}
          placeholder="10 цифр для юр.лица, 12 для ИП"
          inputMode="numeric"
          required
        />
        {!innValid && inn.length > 0 && <Hint $tone="warn">ИНН должен быть 10 или 12 цифр.</Hint>}
        {isFetching && <Hint>Ищу в реестре и ЕГРЮЛ…</Hint>}
        {localFound && (
          <Hint $tone="good">
            <Row>
              <span>
                <strong>{localFound.short_name ?? localFound.full_name}</strong>
              </span>
              <Badge $tone={counterpartyStatusTone(localFound.status)}>
                {counterpartyStatusLabel(localFound.status)}
              </Badge>
              <span style={{ color: 'inherit', opacity: 0.8 }}>· из реестра</span>
            </Row>
            {localFound.address && (
              <div style={{ marginTop: 4, opacity: 0.85 }}>{localFound.address}</div>
            )}
          </Hint>
        )}
        {dangerHint && <Hint $tone={dangerHint.tone}>{dangerHint.text}</Hint>}
        {allowCreate && noLocalMatch && innValid && (
          <Hint $tone={egrulAvailable ? 'info' : 'warn'}>
            <div style={{ marginBottom: 6 }}>
              {egrulAvailable
                ? 'Контрагента нет в реестре. Можно создать и автоматически подтянуть данные из ЕГРЮЛ.'
                : 'Контрагента нет в реестре. ЕГРЮЛ не настроен — введите название вручную.'}
            </div>
            <Button
              type="button"
              $size="sm"
              onClick={onCreate}
              disabled={
                isCreating || (!egrulAvailable && !counterpartyName.trim()) || !can_write
              }
              title={!can_write ? 'Только чтение' : undefined}
            >
              {isCreating ? 'Создаём…' : egrulAvailable ? 'Создать (с ЕГРЮЛ)' : 'Создать вручную'}
            </Button>
            {createError && <div style={{ marginTop: 6 }}>{createError}</div>}
          </Hint>
        )}
      </Field>

      <Field>
        <Label htmlFor="cp-name-picker">Название контрагента</Label>
        <Input
          id="cp-name-picker"
          value={counterpartyName}
          onChange={(e) => onChange({ id: counterpartyId, name: e.target.value })}
          placeholder={
            localFound ? 'Подставлено из реестра' : 'Введите название, если не нашли в ЕГРЮЛ'
          }
          required
          readOnly={!!localFound}
        />
        {localFound && (
          <span
            style={{
              fontSize: 11,
              color: 'var(--osnova-text-muted, #888)',
              marginTop: 4,
            }}
          >
            Имя берётся из реестра и не редактируется здесь — измените его в карточке контрагента.
          </span>
        )}
      </Field>
    </>
  );
}
